/**
 * EngineBadge (Enhanced for Spec 051)
 *
 * Renders engine badge with optional locale flag and model info.
 *
 * Spec 230 restyled this onto `.hub-chip`'s shape. Spec 234 retires the
 * per-engine glaze palette entirely — measured against the light surface, the
 * five glazes fail both the CVD and normal-vision separation floors (spec 234
 * §3), and the legend/badge was the thing that could not rescue identity when
 * the colours failed. An engine is an identity, not a status, and its
 * identity is its name: a neutral chip reading "ChatGPT" says exactly as much
 * as a pastel one, and says it to everybody.
 */
const ENGINE_CONFIG: Record<string, { label: string }> = {
  chatgpt: { label: 'ChatGPT' },
  gemini: { label: 'Gemini' },
  claude: { label: 'Claude' },
  google_aio: { label: 'Google AIO' },
  // Spec 220: mistral is a real engine in the enum and was missing here, so it
  // fell through to the gray fallback and rendered as the raw lowercase id —
  // visibly unlike every other engine wherever a badge appears.
  mistral: { label: 'Mistral' },
};

const LOCALE_FLAGS: Record<string, string> = {
  'en': '\u{1F1FA}\u{1F1F8}',
  'en-US': '\u{1F1FA}\u{1F1F8}',
  'en-GB': '\u{1F1EC}\u{1F1E7}',
  'da': '\u{1F1E9}\u{1F1F0}',
  'da-DK': '\u{1F1E9}\u{1F1F0}',
  'de': '\u{1F1E9}\u{1F1EA}',
  'de-DE': '\u{1F1E9}\u{1F1EA}',
  'fr': '\u{1F1EB}\u{1F1F7}',
  'fr-FR': '\u{1F1EB}\u{1F1F7}',
  'sv': '\u{1F1F8}\u{1F1EA}',
  'sv-SE': '\u{1F1F8}\u{1F1EA}',
  'nb': '\u{1F1F3}\u{1F1F4}',
  'no': '\u{1F1F3}\u{1F1F4}',
  'nb-NO': '\u{1F1F3}\u{1F1F4}',
  'es': '\u{1F1EA}\u{1F1F8}',
  'es-ES': '\u{1F1EA}\u{1F1F8}',
  'it': '\u{1F1EE}\u{1F1F9}',
  'it-IT': '\u{1F1EE}\u{1F1F9}',
  'pt': '\u{1F1F5}\u{1F1F9}',
  'pt-BR': '\u{1F1E7}\u{1F1F7}',
  'nl': '\u{1F1F3}\u{1F1F1}',
  'nl-NL': '\u{1F1F3}\u{1F1F1}',
  'ja': '\u{1F1EF}\u{1F1F5}',
  'ko': '\u{1F1F0}\u{1F1F7}',
  'zh': '\u{1F1E8}\u{1F1F3}',
};

const MODEL_DISPLAY: Record<string, string> = {
  'gpt-4o': 'GPT-4o',
  'gpt-4o-2024-08-06': 'GPT-4o',
  'gpt-4o-mini': 'GPT-4o Mini',
  'gemini-2.5-flash': 'Gemini 2.5 Flash',
  'gemini-2.0-flash': 'Gemini 2.0 Flash',
  'gemini-1.5-pro': 'Gemini 1.5 Pro',
  'claude-sonnet-4-20250514': 'Claude Sonnet 4',
  'claude-3-5-sonnet-20241022': 'Claude 3.5 Sonnet',
};

interface EngineBadgeProps {
  engine: string;
  locale?: string | null;
  model?: string | null;
  className?: string;
}

export function EngineBadge({ engine, locale, model, className = '' }: EngineBadgeProps) {
  const config = ENGINE_CONFIG[engine] ?? { label: engine };

  const flag = locale ? LOCALE_FLAGS[locale] ?? null : null;
  const modelDisplay = model ? MODEL_DISPLAY[model] ?? model : null;

  return (
    <span className={`hub-chip ${className}`}>
      {config.label}
      {modelDisplay && <span className="opacity-70">/ {modelDisplay}</span>}
      {flag && <span className="ml-0.5">{flag}</span>}
    </span>
  );
}
